import React, { useState, useRef, useEffect } from 'react';
import { SparklesCore } from './ui/sparkles';

/**
 * PromoVideo - Plays the AI-generated promotional video
 *
 * Features:
 * - Auto-plays on scroll into view
 * - Loops seamlessly
 * - Falls back to static content if video unavailable
 * - Muted by default (can unmute)
 * - Sparkles effect projecting from bottom edge
 */
export default function PromoVideo({ className = '' }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // For now, use concatenated video or first segment
  const videoSrc = '/promo-videos/emailai-promo.mp4';
  const fallbackSrc = '/promo-videos/01-integrations-hook.mp4';

  // Intersection Observer for auto-play
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && videoRef.current && isLoaded) {
            videoRef.current.play().catch(() => {});
            setIsPlaying(true);
          } else if (videoRef.current) {
            videoRef.current.pause();
            setIsPlaying(false);
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, [isLoaded]);

  const handleError = () => {
    // Try fallback video
    if (videoRef.current && videoRef.current.src !== fallbackSrc) {
      videoRef.current.src = fallbackSrc;
    } else {
      setHasError(true);
    }
  };

  const handleLoaded = () => {
    setIsLoaded(true);
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  if (hasError) {
    return null; // Fall back to static content
  }

  return (
    <div ref={containerRef} className={`promo-video-container ${className}`}>
      {/* Video wrapper */}
      <div className="promo-video-wrapper">
        <video
          ref={videoRef}
          className="promo-video"
          src={videoSrc}
          muted
          loop
          playsInline
          onError={handleError}
          onLoadedData={handleLoaded}
          onClick={togglePlay}
        />

        {/* Play/Pause overlay */}
        {isLoaded && (
          <button
            className="promo-video-control"
            onClick={togglePlay}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {!isPlaying && (
              <svg viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>
        )}

        {/* Loading state */}
        {!isLoaded && !hasError && (
          <div className="promo-video-loading">
            <div className="promo-video-spinner" />
          </div>
        )}
      </div>

      {/* Sparkles effect - constrained to video width */}
      <div className="promo-video-sparkles-container">
        {/* Gradient glow lines - the "lamp" effect */}
        <div className="promo-video-glow-lines">
          <div className="promo-video-glow-outer" />
          <div className="promo-video-glow-outer-sharp" />
        </div>
        <div className="promo-video-glow-lines">
          <div className="promo-video-glow-inner" />
          <div className="promo-video-glow-inner-sharp" />
        </div>

        {/* Sparkles particles - semi-circular emanation */}
        <div className="promo-video-sparkles-mask">
          <SparklesCore
            background="transparent"
            minSize={0.3}
            maxSize={1.2}
            particleDensity={350}
            className="w-full h-full"
            particleColor="#FFFFFF"
            speed={2}
          />
        </div>
      </div>
    </div>
  );
}
